// useLayoutEditorBlockModalState.js

import { useCallback, useState } from 'react';

// Utilities
import { normalizeFieldBlockFieldKeys } from './layoutFieldDisplay';
import { getNodeBlockKey } from './pageEditorLayoutUtils';

function normalizeString(value) {
	return String(value || '').trim();
}

/**
 * useLayoutEditorBlockModalState
 *
 * Owns the generic block-modal state used by LayoutEditor for page settings, fields, nodes, rich text, and rollups.
 *
 * @param {object} params - Hook parameters.
 * @param {object} params.blocksByKey - Current block lookup map.
 * @param {function|null} [params.dismissMenu=null] - Optional callback to dismiss the slot menu before opening a modal.
 * @param {function} params.getResolvedBlockValue - Resolves block overrides for a given block key.
 * @param {object} params.layout - Current layout tree.
 * @param {function|null} [params.onClearInlineRichTextEditing=null] - Optional callback to clear inline rich-text editing before opening the rich-text modal.
 * @returns {object} Block-modal state values and handlers.
 */
const useLayoutEditorBlockModalState = ({
	blocksByKey,
	dismissMenu = null,
	getResolvedBlockValue,
	layout,
	onClearInlineRichTextEditing = null,
}) => {
	const [fieldBlockModalOpen, setFieldBlockModalOpen] = useState(false);
	const [fieldBlockDraftFieldKeys, setFieldBlockDraftFieldKeys] = useState([]);
	const [fieldBlockSlotKey, setFieldBlockSlotKey] = useState('');
	const [nodeBlockModalOpen, setNodeBlockModalOpen] = useState(false);
	const [nodeBlockSlotKey, setNodeBlockSlotKey] = useState('');
	const [richTextBlockModalOpen, setRichTextBlockModalOpen] = useState(false);
	const [richTextBlockSlotKey, setRichTextBlockSlotKey] = useState('');
	const [rollupBlockModalOpen, setRollupBlockModalOpen] = useState(false);
	const [rollupBlockSlotKey, setRollupBlockSlotKey] = useState('');

	/**
	 * handleCloseFieldBlockModal
	 *
	 * Closes the field picker modal and clears its draft state.
	 */
	const handleCloseFieldBlockModal = useCallback(() => {
		setFieldBlockModalOpen(false);
		setFieldBlockDraftFieldKeys([]);
		setFieldBlockSlotKey('');
	}, []);

	/**
	 * handleCloseNodeBlockModal
	 *
	 * Closes the node picker modal.
	 */
	const handleCloseNodeBlockModal = useCallback(() => {
		setNodeBlockModalOpen(false);
		setNodeBlockSlotKey('');
	}, []);

	/**
	 * handleCloseRichTextBlockModal
	 *
	 * Closes the rich-text block modal.
	 */
	const handleCloseRichTextBlockModal = useCallback(() => {
		setRichTextBlockModalOpen(false);
		setRichTextBlockSlotKey('');
	}, []);

	/**
	 * handleCloseRollupBlockModal
	 *
	 * Closes the rollup picker modal.
	 */
	const handleCloseRollupBlockModal = useCallback(() => {
		setRollupBlockModalOpen(false);
		setRollupBlockSlotKey('');
	}, []);

	/**
	 * handleOpenFieldBlockModal
	 *
	 * Opens the field picker for the requested slot and seeds it with the current field selection.
	 *
	 * @param {string} slotKey - Target slot key.
	 */
	const handleOpenFieldBlockModal = useCallback((slotKey) => {
		const cleanedSlotKey = normalizeString(slotKey);
		if (!cleanedSlotKey) return;

		const slotNode = layout.nodes[cleanedSlotKey] || null;
		const blockKey = getNodeBlockKey(slotNode);
		const block = blockKey ? blocksByKey[blockKey] || null : null;
		const resolvedBlock = getResolvedBlockValue(blockKey, block);

		dismissMenu?.();
		setFieldBlockDraftFieldKeys(normalizeFieldBlockFieldKeys(resolvedBlock?.fieldKeys));
		setFieldBlockSlotKey(cleanedSlotKey);
		setFieldBlockModalOpen(true);
	}, [blocksByKey, dismissMenu, getResolvedBlockValue, layout.nodes]);

	/**
	 * handleOpenNodeBlockModal
	 *
	 * Opens the node picker for the requested slot.
	 *
	 * @param {string} slotKey - Target slot key.
	 */
	const handleOpenNodeBlockModal = useCallback((slotKey) => {
		const cleanedSlotKey = normalizeString(slotKey);
		if (!cleanedSlotKey) return;

		dismissMenu?.();
		setNodeBlockSlotKey(cleanedSlotKey);
		setNodeBlockModalOpen(true);
	}, [dismissMenu]);

	/**
	 * handleOpenRichTextBlockModal
	 *
	 * Opens the rich-text block modal for the requested slot.
	 *
	 * @param {string} slotKey - Target slot key.
	 */
	const handleOpenRichTextBlockModal = useCallback((slotKey) => {
		const cleanedSlotKey = normalizeString(slotKey);
		if (!cleanedSlotKey) return;

		dismissMenu?.();
		onClearInlineRichTextEditing?.();
		setRichTextBlockSlotKey(cleanedSlotKey);
		setRichTextBlockModalOpen(true);
	}, [dismissMenu, onClearInlineRichTextEditing]);

	/**
	 * handleOpenRollupBlockModal
	 *
	 * Opens the rollup picker for the requested slot.
	 *
	 * @param {string} slotKey - Target slot key.
	 */
	const handleOpenRollupBlockModal = useCallback((slotKey) => {
		const cleanedSlotKey = normalizeString(slotKey);
		if (!cleanedSlotKey) return;

		dismissMenu?.();
		setRollupBlockSlotKey(cleanedSlotKey);
		setRollupBlockModalOpen(true);
	}, [dismissMenu]);

	/**
	 * handleFieldBlockDraftFieldKeysChange
	 *
	 * Updates the draft field list for the field picker modal.
	 *
	 * @param {array} nextFieldKeys - Next selected field keys.
	 */
	const handleFieldBlockDraftFieldKeysChange = useCallback((nextFieldKeys) => {
		setFieldBlockDraftFieldKeys(normalizeFieldBlockFieldKeys(nextFieldKeys));
	}, []);

	return {
		fieldBlockDraftFieldKeys,
		fieldBlockModalOpen,
		fieldBlockSlotKey,
		handleCloseFieldBlockModal,
		handleCloseNodeBlockModal,
		handleCloseRichTextBlockModal,
		handleCloseRollupBlockModal,
		handleFieldBlockDraftFieldKeysChange,
		handleOpenFieldBlockModal,
		handleOpenNodeBlockModal,
		handleOpenRichTextBlockModal,
		handleOpenRollupBlockModal,
		nodeBlockModalOpen,
		nodeBlockSlotKey,
		richTextBlockModalOpen,
		richTextBlockSlotKey,
		rollupBlockModalOpen,
		rollupBlockSlotKey,
	};
};

export default useLayoutEditorBlockModalState;